import React, { useEffect, useState } from "react";
import "./PlantationTwoBanners.scss";
import { useCollection } from "../../../../firebase-files/hooks/useCollection";
import { useDocument } from "../../../../firebase-files/hooks/useDocument";
import { useAuthContext } from "../../../../firebase-files/hooks/useAuthContext";
import { useFirestore } from "../../../../firebase-files/hooks/useFirestore";
import firebase from "firebase/app";
import "firebase/storage";
import "firebase/firestore";

const PlantationTwoBanners = () => {
  const [current, setCurrent] = useState(0);
  const [formVisible, setFormVisible] = useState(false);
  const [form, setForm] = useState({ imageFile: null });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [deletePopup, setDeletePopup] = useState(false);
  const [appType, setAppType] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const { user } = useAuthContext();
  const { documents: banners } = useCollection("banners");
  const { documents: users } = useCollection("users");
  const { document: appTypeDoc } = useDocument("settings", "AppType");
  const { addDocument, deleteDocument } = useFirestore("banners");

  // ✅ check if current user is admin or superAdmin
  useEffect(() => {
    if (!user || !users) {
      setIsAdmin(false);
      setIsSuperAdmin(false);
      return;
    }

    const phone = user?.phoneNumber?.replace("+91", "91");
    const userInfo = users.find((u) => u.id === phone);

    if (userInfo) {
      setIsAdmin(userInfo.role?.toLowerCase() === "admin");
      setIsSuperAdmin(userInfo.role?.toLowerCase() === "superadmin");
    } else {
      setIsAdmin(false);
      setIsSuperAdmin(false);
    }
  }, [user, users]);

  // ✅ Get apptype from appTypeDoc
  useEffect(() => {
    if (appTypeDoc) {
      if (appTypeDoc.AppType) {
        setAppType(appTypeDoc.AppType);
      } else if (appTypeDoc.apptype) {
        setAppType(appTypeDoc.apptype);
      }
    }
  }, [appTypeDoc]);

  // Slideshow auto-rotate
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrent((prev) => (banners?.length ? (prev + 1) % banners.length : 0));
    }, 5000);
    return () => clearInterval(interval);
  }, [banners]);

  // Check if user can add banners
  const canAdd = () => {
    if (!user) return false;
    const currentAppType = appType || "lite";
    if (currentAppType === "pro") {
      return isAdmin || isSuperAdmin;
    } else {
      return isSuperAdmin;
    }
  };

  // Check if user can delete banners (only superadmin)
  const canDelete = () => {
    if (!user) return false;
    return isSuperAdmin;
  };

  const handleChange = (e) => {
    const { files } = e.target;
    if (files && files[0]) {
      setForm({ imageFile: files[0] });
      setPreviewUrl(URL.createObjectURL(files[0]));
    }
  };

  const handleCancel = () => {
    setFormVisible(false);
    setForm({ imageFile: null });
    setPreviewUrl(null);
    setSuccess("");
    setErrorMsg("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.imageFile) return alert("Please select an image");

    setLoading(true);
    setSuccess("");
    setErrorMsg("");

    try {
      // 1. Create empty banner document first to get documentId
      const newDoc = await addDocument({});
      const documentId = newDoc.id;

      // 2. Upload image to Firebase Storage
      const storageRef = firebase.storage().ref();
      const imageRef = storageRef.child(
        `banners/${documentId}/${Date.now()}_${form.imageFile.name}`
      );
      await imageRef.put(form.imageFile);
      const imageUrl = await imageRef.getDownloadURL();

      // 3. Update the document with metadata
      await firebase.firestore().collection("banners").doc(documentId).update({
        imageUrl,
        createdAt: new Date(),
        createdBy: user?.uid,
      });

      setSuccess("✅ Banner added successfully!");
      setForm({ imageFile: null });
      setPreviewUrl(null);
      setFormVisible(false);
    } catch (err) {
      console.error("Error uploading banner:", err);
      setErrorMsg("❌ Failed to upload banner.");
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    const bannerToDelete = banners[current];
    if (!bannerToDelete) return;

    try {
      const documentId = bannerToDelete.id;
      const folderPath = `banners/${documentId}`;
      const storageRef = firebase.storage().ref(folderPath);
      const listResult = await storageRef.listAll();
      const deletePromises = listResult.items.map((item) => item.delete());
      await Promise.all(deletePromises);
      await deleteDocument(documentId);
      setDeletePopup(false);
    } catch (err) {
      console.error("Failed to delete banner or storage files:", err);
    }
  };

  return (
    <div className="plantationTwo-Banners">
      {/* Slide Show */}
      {banners &&
        banners.length > 0 &&
        banners.map((banner, index) => (
          <div
            key={banner.id}
            className={`slide ${index === current ? "active" : ""}`}
          >
            <img
              src={banner.imageUrl}
              alt={`Banner ${index + 1}`}
              className="slide-img"
            />
          </div>
        ))}

      {/* Navigation Dots */}
      <div className="dots">
        {banners &&
          banners.map((_, index) => (
            <span
              key={index}
              className={`dot ${index === current ? "active" : ""}`}
              onClick={() => setCurrent(index)}
            />
          ))}
      </div>

      {/* ✅ Admin Control Buttons */}
      {user && (
        <div className="add-banner-btn-on-banner">
          {canAdd() && (
            <button onClick={() => setFormVisible(true)}>➕ Add Banner</button>
          )}
          {canDelete() && banners?.length > 0 && (
            <button className="delete-btn" onClick={() => setDeletePopup(true)}>
              🗑️ Delete
            </button>
          )}
        </div>
      )}

      {/* ✅ Add Banner Popup */}
      {formVisible && (
        <div className="add-banner-popup" onClick={handleCancel}>
          <div
            className="form-container"
            onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside
          >
            <form onSubmit={handleSubmit}>
              <h3>Add New Banner</h3>
              <input type="file" accept="image/*" onChange={handleChange} />

              {previewUrl && (
                <div className="image-preview">
                  <img src={previewUrl} alt="Preview" />
                </div>
              )}

              <div className="button-row">
                <button
                  type="submit"
                  className="add-btn"
                  disabled={loading}
                >
                  {loading ? "Uploading..." : "Upload"}
                </button>
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>

              {success && <p className="success">{success}</p>}
              {errorMsg && <p className="error">{errorMsg}</p>}
            </form>
          </div>
        </div>
      )}

      {/* ✅ Delete Confirmation Popup */}
      {deletePopup && (
        <div className="delete-popup">
          <div className="popup-content">
            <p>Are you sure you want to delete this banner?</p>
            <div className="popup-buttons">
              <button
                className="confirm-btn"
                onClick={handleConfirmDelete}
              >
                Yes, Delete
              </button>
              <button
                className="cancel-btn"
                onClick={() => setDeletePopup(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlantationTwoBanners;
